import * as React from 'react';

import type { HeadProps, PageProps } from 'gatsby';
import { StaticImage } from 'gatsby-plugin-image';

import { HeroBlock } from 'stile-shared/src/components/2020/blocks/HeroBlock';
import { PageLinksBlock } from 'stile-shared/src/components/2020/blocks/PageLinksBlock';
import { TeaserBlock } from 'stile-shared/src/components/2020/blocks/TeaserBlock';
import { TextBlock } from 'stile-shared/src/components/2020/blocks/TextBlock';
import { Link } from 'stile-shared/src/components/2020/common/Link';
import { H3, P, Spacer, Ul } from 'stile-shared/src/components/2020/common/Primitives';
import { Column } from 'stile-shared/src/components/2020/layout/Column';
import { theme } from 'stile-shared/src/styles/2020/theme';

import { PageLayout } from 'templates/2020/PageLayout';
import { SEO } from 'templates/2020/SEO';

import animatedCoverImage from '../../../stile-shared/assets/images/stile-x-digital/stile-x-digital-cover.gif';

export function Head(props: HeadProps) {
  return (
    <SEO
      title="Stile X is now online!"
      description="Access digital copies of your favourite Stile X resources directly in Stile"
      slug={props.location.pathname}
    />
  );
}

function HomePage(props: PageProps) {
  return (
    <PageLayout {...props}>
      <HeroBlock
        title="Stile X is now online!"
        text="Access digital copies of your favourite Stile X resources directly in Stile"
        variant="tight"
        image={
          <img
            src={animatedCoverImage}
            alt="A gif showing Stile X booklets being transferred into digital documents in Stile."
          />
        }
      />

      <TextBlock>
        <P>
          You asked, we listened. You can now simply and easily access Stile X resources in Stile.
          Find digital versions of everything from practice tests, glossary flashcards and even
          worksheets with new examples for teachers.
        </P>
        <P>
          To see these exciting updates, make sure you’ve{' '}
          <Link url="https://help.stileapp.com/hc/en-us/articles/9068486052751-Copy-a-unit-from-the-Stile-library">
            checked out the latest version of a unit from the Stile Library.
          </Link>{' '}
        </P>
      </TextBlock>

      <TeaserBlock
        title="What’s new?"
        variant="dark"
        image={
          <StaticImage
            src="../../../stile-shared/assets/images/stile-x-digital/stile-x-digital-features.png"
            alt="A close up of a Stile X worksheet, with a hand filling in one of the questions"
          />
        }
      >
        <Column space={theme.space.m}>
          <Column space={theme.space.xxs}>
            <P bold>1. Printable Stile X revision worksheets with exemplars</P>
            <P>
              Every Stile X revision booklet has been broken down into printable worksheets! Print
              out any section as needed. As PDFs, you can easily project these revision worksheets
              to go through the questions as a class.
            </P>
          </Column>
          <Column space={theme.space.xxs}>
            <P bold>2. Printable glossary flashcards</P>
            <P>
              We’ve added printable versions of our unit-relevant glossary flashcards in Stile.
              Incorporating the evidence-based learning strategy of spaced repetition, you’ll find
              flashcards and glossaries throughout the Stile X booklets and Stile X phone app.
            </P>
          </Column>
          <Column space={theme.space.xxs}>
            <P bold>3. Printable practice tests</P>
            <P>
              Similarly, the Stile X practice tests are now available as printable PDFs. This
              matches nicely with the practice test model answers already available in Stile.
            </P>
          </Column>
        </Column>
      </TeaserBlock>

      <TeaserBlock
        title="How to find the resources?"
        image={
          <StaticImage
            src="../../../stile-shared/assets/images/stile-x-digital/stile-x-digital-location.png"
            alt="A close up of a Stile X worksheet, with a hand filling in one of the questions"
          />
        }
      >
        <Spacer />
        <Column space={theme.space.xl}>
          <Column space={theme.space.xxs}>
            <H3>In your subject</H3>
            <P>
              For each of your folders, you will find a new section called Teaching resources and
              within that a section called Stile X resources. Here you can find
            </P>
            <Ul space={theme.space.xxxs}>
              <li>
                <strong>Practice tests and model answers</strong>
              </li>
              <li>
                <strong>Printable flashcards</strong>
              </li>
            </Ul>
            <P italic>
              Note you may need to check out a new version from the library, but over the next few
              weeks these will magically appear.
            </P>
          </Column>

          <Column space={theme.space.xxs}>
            <H3>In a classroom lesson</H3>
            <P>
              All units in Stile include{' '}
              <Link
                openInNewTab
                url="https://blog.stileeducation.com/variety-is-the-spice-of-classroom-life-2/"
              >
                a variety of lesson types
              </Link>
              , classroom lessons cover core curriculum requirements. Within classroom lessons you
              can find the relevant{' '}
              <strong>Stile X revision activity and marked up example responses.</strong>
            </P>
            <P>
              These can be found at the top of a lesson in prepare mode, so you can print them
              before class, or at the bottom of the lesson for both you and your students so you can
              project and display the example responses.
            </P>
            <P>
              <Link
                openInNewTab
                url="https://stileapp.com/releaseNotes/printable-revision-and-mastery-resources-are-now-available-in-stile/1c485b77-76c3-435d-abba-df77bfbb2a50"
              >
                Learn more about these updates.
              </Link>
            </P>
          </Column>
        </Column>
      </TeaserBlock>

      <PageLinksBlock
        title="Need a refresher on Stile X?"
        text="We’re hosting a series of free 30-minute webinars on Stile and Stile X. "
        button={{
          url: 'https://www.bigmarker.com/communities/stile-education1/conferences',
          text: 'Register here',
          openInNewTab: true,
        }}
      />
    </PageLayout>
  );
}

export default HomePage;
